import React, { useEffect, useRef, useState, useCallback } from "react";
import { Camera, CameraOff, Mic, MicOff, RefreshCw, AlertTriangle, Sparkles } from "lucide-react";

// localStorage key for the beauty-filter preference. Persisted so a
// creator who once turned it on doesn't have to re-toggle on every
// go-live session — it's a self-confidence setting, not a per-stream
// choice.
const BEAUTY_KEY = "neosync:live:beauty";
const BEAUTY_FILTER = "blur(0.5px) contrast(1.08) saturate(1.1) brightness(1.05)";

/**
 * Pre-flight camera + mic check for Go Live. Renders a live mirror of
 * whatever camera the user has connected + a real-time green mic-level
 * meter driven by Web Audio's AnalyserNode. The moment the creator sees
 * their own face + green bars responding to their voice, they know the
 * stream isn't going to be a black-screen accident.
 *
 * Emits `onReady({video, audio})` upward once the tracks are running
 * cleanly so the parent's "GO LIVE NOW" button can enable. The MediaStream
 * itself is torn down when the component unmounts — the actual live
 * broadcast opens its own new stream via LiveKit's `<LiveKitRoom>`.
 *
 * Design notes:
 *  * Mirroring — camera is CSS-flipped (scaleX -1). This matches every
 *    "selfie view" convention on TikTok/IG/Zoom and prevents the "wait,
 *    which way should I move?" moment.
 *  * Permission failures fall back to a big red retry card explaining
 *    why (blocked / no device / in use by another app).
 *  * Mic meter uses `smoothingTimeConstant=0.8` so the bar isn't jittery.
 */
export default function LivePreflight({ onReady, onStatusChange, onBeautyChange }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const [meterLevel, setMeterLevel] = useState(0);   // 0..1
  const [videoOn, setVideoOn] = useState(true);
  const [audioOn, setAudioOn] = useState(true);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [beauty, setBeauty] = useState(() => {
    try { return localStorage.getItem(BEAUTY_KEY) === "1"; }
    catch { return false; }
  });

  // Persist the beauty toggle + notify parent (so the actual LiveKit
  // publisher can apply the same CSS filter to the creator's own view
  // of themselves once broadcasting).
  useEffect(() => {
    try { localStorage.setItem(BEAUTY_KEY, beauty ? "1" : "0"); } catch { /* private mode */ }
    onBeautyChange?.(beauty);
  }, [beauty, onBeautyChange]);

  const stopStream = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setReady(false);
  }, []);

  const startPreview = useCallback(async () => {
    stopStream();
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoOn ? { facingMode: "user", width: 1280, height: 720 } : false,
        audio: audioOn,
      });
      streamRef.current = stream;
      if (videoRef.current && videoOn) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      if (audioOn) {
        // Wire up the mic meter. AnalyserNode gives us fresh amplitude
        // data each frame; we take the peak of `getByteTimeDomainData`
        // (values 0..255 with 128 = silence) and normalize to 0..1.
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC();
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.8;
        src.connect(analyser);
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
        const buf = new Uint8Array(analyser.fftSize);
        const tick = () => {
          if (!analyserRef.current) return;
          analyserRef.current.getByteTimeDomainData(buf);
          let peak = 0;
          for (let i = 0; i < buf.length; i++) {
            const v = Math.abs(buf[i] - 128) / 128;
            if (v > peak) peak = v;
          }
          setMeterLevel(peak);
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      }
      setReady(true);
      onReady?.({ hasVideo: videoOn, hasAudio: audioOn });
      onStatusChange?.({ ready: true, error: null });
    } catch (err) {
      const name = err?.name || "";
      let msg;
      if (name === "NotAllowedError" || name === "SecurityError") {
        msg = "Camera / mic blocked. Click the lock icon in your browser bar and allow access.";
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        msg = "No camera or microphone found on this device.";
      } else if (name === "NotReadableError") {
        msg = "Camera / mic is already in use by another app (Zoom, OBS, etc.). Close it and retry.";
      } else {
        msg = err?.message || "Couldn't access camera / mic.";
      }
      setError(msg);
      onStatusChange?.({ ready: false, error: msg });
    }
  }, [videoOn, audioOn, onReady, onStatusChange, stopStream]);

  // Start on mount, restart whenever the video/audio toggles change.
  useEffect(() => {
    startPreview();
    return stopStream;
  }, [videoOn, audioOn, startPreview, stopStream]);

  // Meter bar visual — 16 bars, filled proportionally to `meterLevel`.
  const bars = Array.from({ length: 16 });
  const litCount = Math.round(meterLevel * bars.length * 1.4); // exaggerate a bit

  return (
    <div className="bg-black border border-[#39FF14]/30 relative overflow-hidden" data-testid="live-preflight">
      {/* Mirror */}
      <div className="relative aspect-[9/16] sm:aspect-video bg-[#050505]">
        {videoOn ? (
          <video
            ref={videoRef}
            className="w-full h-full object-cover transition-[filter] duration-300"
            style={{
              transform: "scaleX(-1)",   // selfie mirror
              filter: beauty ? BEAUTY_FILTER : "none",
            }}
            playsInline
            muted
            autoPlay
            data-testid="live-preflight-video"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-white/40">
            <CameraOff className="w-10 h-10" />
            <p className="font-mono text-xs uppercase tracking-widest">// camera.disabled</p>
          </div>
        )}

        {/* Live-ish badge (says PREVIEW, not LIVE) */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="bg-[#39FF14] text-black text-[10px] font-black px-2 py-0.5 flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-black animate-pulse" />
            PREVIEW
          </span>
          {ready && !error && (
            <span className="bg-black/70 border border-[#39FF14] text-[#39FF14] text-[10px] font-mono px-2 py-0.5">
              // signal.ok
            </span>
          )}
        </div>

        {/* Error overlay */}
        {error && (
          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-3 p-6 text-center">
            <AlertTriangle className="w-8 h-8 text-[#FF007F]" />
            <p className="text-white text-sm max-w-xs">{error}</p>
            <button
              onClick={startPreview}
              data-testid="live-preflight-retry"
              className="mt-2 px-4 py-2 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors text-xs font-mono uppercase tracking-widest flex items-center gap-2"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        )}

        {/* Mic-level meter — horizontal bar at the bottom */}
        {audioOn && !error && (
          <div className="absolute bottom-3 inset-x-3 flex items-center gap-2" data-testid="live-preflight-meter">
            <Mic className="w-3.5 h-3.5 text-[#39FF14] shrink-0" />
            <div className="flex-1 flex items-center gap-[2px] h-3">
              {bars.map((_, i) => {
                const lit = i < litCount;
                // Color grades: green for the first 60%, yellow next 25%,
                // pink for peak — so the creator can see they're too loud.
                const color =
                  i < bars.length * 0.6 ? "bg-[#39FF14]" :
                  i < bars.length * 0.85 ? "bg-[#FFD93D]" :
                  "bg-[#FF007F]";
                return (
                  <span
                    key={i}
                    className={`flex-1 h-full transition-opacity duration-75 ${color} ${lit ? "opacity-100" : "opacity-15"}`}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-white/10 bg-[#0A0A0C]">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setVideoOn((v) => !v)}
            data-testid="live-preflight-toggle-video"
            className={`p-2 border transition-colors ${videoOn ? "border-[#39FF14] text-[#39FF14]" : "border-white/20 text-white/40"}`}
            aria-label={videoOn ? "Disable camera" : "Enable camera"}
          >
            {videoOn ? <Camera className="w-4 h-4" /> : <CameraOff className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setAudioOn((a) => !a)}
            data-testid="live-preflight-toggle-audio"
            className={`p-2 border transition-colors ${audioOn ? "border-[#39FF14] text-[#39FF14]" : "border-white/20 text-white/40"}`}
            aria-label={audioOn ? "Mute mic" : "Unmute mic"}
          >
            {audioOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setBeauty((b) => !b)}
            data-testid="live-preflight-toggle-beauty"
            className={`p-2 border transition-colors flex items-center gap-1 ${beauty ? "border-[#FF007F] text-[#FF007F] bg-[#FF007F]/10 shadow-[0_0_12px_rgba(255,0,127,0.35)]" : "border-white/20 text-white/40"}`}
            aria-label={beauty ? "Disable beauty filter" : "Enable beauty filter"}
            aria-pressed={beauty}
            title="Beauty filter — smooth skin + boost warmth"
          >
            <Sparkles className="w-4 h-4" />
            <span className="text-[10px] font-mono uppercase tracking-widest hidden sm:inline">
              beauty
            </span>
          </button>
        </div>
        <p className="text-[10px] font-mono text-white/40 uppercase tracking-widest">
          {ready ? "// tap.speak to test mic" : "// waiting.for.devices"}
        </p>
      </div>
    </div>
  );
}
